import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { Co2Outlined, InfoOutlined } from '@mui/icons-material';
import { CuriosityNamespaceType, useCuriosityTranslation } from '@procureai/curiosity-ui-mui';
import {
  NumberFormatPreset,
  PieChart,
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetIcon,
  formatNumber,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { isEmpty, isNil } from 'ramda';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { TooltipTypography } from '~/components/TooltipTypography/TooltipTypography';
import { lastYearDateRange } from '~/util/cube';

const formatValue = formatNumber(NumberFormatPreset.INTEGER_COMPACT);

export const EmissionCommodityWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'EmissionCommodityWidget' });
  const [widgetRef, active] = useScrollIntoView({ hash: 'emission-commodity' });
  const { ct } = useCuriosityTranslation(CuriosityNamespaceType.COMMODITY_GROUP);
  const [parentCommodityGroup /* setParentCommodityGroup */] = useState<string>();
  const cube = parentCommodityGroup ? 'EmissionsBySupplierChildren' : 'EmissionsBySupplierRoot';

  const filters: Filter[] = [
    {
      member: `${cube}.supplierId`,
      operator: 'equals',
      values: [entityId],
    },
    ...(parentCommodityGroup
      ? [{ member: `${cube}.commodityGroupId`, operator: 'equals', values: [parentCommodityGroup] } as Filter]
      : [{ member: `${cube}.commodityGroupId`, operator: 'set' } as Filter]),
  ];

  const query = useCubeQuery({
    measures: [`${cube}.totalEmissionValue`],
    dimensions: [`${cube}.commodityGroupId`],
    timeDimensions: [
      {
        dimension: `${cube}.date`,
        dateRange: lastYearDateRange,
      },
    ],
    order: [[`${cube}.totalEmissionValue`, 'desc']],
    limit: 5,
    filters,
  });

  const data = query.resultSet
    ?.rawData()
    ?.map((segment) => ({
      commodityGroup: ct(String(segment[`${cube}.commodityGroupId`]))?.value ?? '',
      value: Number(segment[`${cube}.totalEmissionValue`]),
    }))
    .filter((item) => item.commodityGroup !== 'null');

  const totalValue = data?.reduce((p, c) => p + c.value, 0) ?? 0;
  const totalValueLabel = `${formatValue(totalValue)} KG`;
  const isLoading = query.isLoading;
  const isDataEmpty = isEmpty(data) || isNil(data);

  return (
    <Widget hover="js" ref={widgetRef} active={active} data-intercom-target="EmissionCommodityWidget">
      <WidgetHeader
        title={t('title')}
        subTitle={t('subTitle')}
        icon={<WidgetIcon icon={<Co2Outlined fontSize="small" />} />}
        actionsRight={
          <TooltipTypography arrow title={t('info.tooltip')}>
            <InfoOutlined sx={{ color: 'text.secondary' }} />
          </TooltipTypography>
        }
      />
      <WidgetContent>
        {!isDataEmpty ? (
          <PieChart
            legend
            data={data}
            dataKey="value"
            nameKey="commodityGroup"
            label={totalValueLabel}
            valueFormatter={(value) => `${formatValue(value)} KG`}
          />
        ) : isLoading ? (
          <CircularProgressCentered sx={{ position: 'relative' }} />
        ) : (
          <PlaceholderWidget />
        )}
      </WidgetContent>
    </Widget>
  );
};
